export const studioApiEndpoints = {
  auth: {
    register: "auth/register",
    login: "auth/login",
    refreshToken: "auth/refresh",
    profile: "auth/profile",
    logout: "auth/logout",
    forgotPassword: "auth/forgot-password",
    resetPassword: "auth/reset-password",
    changePassword: "auth/change-password",
    verifyEmail: "auth/verify-email",
    resendVerification: "auth/resend-verification",
    selectOrganization: "auth/select-organization",
    switchOrganization: "auth/switch-organization",
    organizations: "auth/organizations",
    invitations: "auth/invitations",
    acceptInvitation: (id: number) => `auth/invitations/${id}/accept`,
    declineInvitation: (id: number) => `auth/invitations/${id}/decline`,
  },
  organizations: {
    current: "organizations/current",
    featureFlags: "organizations/current/feature-flags",
    members: "organizations/current/members",
    invite: "organizations/current/invite",
    member: (userId: number) => `organizations/current/members/${userId}`,
    memberRole: (userId: number) =>
      `organizations/current/members/${userId}/role`,
    invitations: "organizations/current/invitations",
    invitation: (id: number) => `organizations/current/invitations/${id}`,
  },
  tasks: {
    tasks: (projectId: number) => `task/${projectId}`,
    task: (projectId: number, taskId: number) => `task/${projectId}/${taskId}`,
    uploadUrl: (projectId: number) => `task/${projectId}/upload-url`,
    confirm: (projectId: number) => `task/${projectId}/confirm`,
    export: (projectId: number) => `task/${projectId}/export`,
    ids: (projectId: number) => `task/${projectId}/ids`,
    history: (projectId: number, taskId: number) =>
      `task/${projectId}/${taskId}/history`,
    importedEvents: (projectId: number) => `task/${projectId}/imported-events`,
    import: (projectId: number) => `task/${projectId}/import`,
    importedSourceTasks: (projectId: number) =>
      `task/${projectId}/imported-source-tasks`,
  },
  projects: {
    projects: "projects",
    project: (projectId: number) => `projects/${projectId}`,
    projectLabels: (projectId: number) => `project-labels/${projectId}`,
    projectLabel: (projectId: number, labelId: number) =>
      `project-labels/${projectId}/${labelId}`,
    projectLabelsArchived: (projectId: number) =>
      `project-labels/${projectId}/archived`,
    projectLabelArchive: (projectId: number, labelId: number) =>
      `project-labels/${projectId}/${labelId}/archive`,
    projectLabelRestore: (projectId: number, labelId: number) =>
      `project-labels/${projectId}/${labelId}/restore`,
    projectLabelUsage: (projectId: number, labelId: number) =>
      `project-labels/${projectId}/${labelId}/usage`,
    models: {
      models: (projectId: number) => `model/${projectId}`,
    },
    // Org-scoped rather than project-scoped, hence outside `models` above.
    trainingUsage: "training-usage",
    dashboardConfig: {
      configurations: (projectId: number) =>
        `dashboard-config/${projectId}/configurations`,
      configuration: (projectId: number, configId: number) =>
        `dashboard-config/${projectId}/configurations/${configId}`,
      deployPayload: (projectId: number, configId: number) =>
        `dashboard-config/${projectId}/configurations/${configId}/deploy-payload`,
    },
  },
  capturedVideos: {
    list: (projectId: number) => `captured-video/${projectId}`,
    single: (projectId: number, videoId: number) =>
      `captured-video/${projectId}/${videoId}`,
    uploadUrl: (projectId: number) => `captured-video/${projectId}/upload-url`,
    confirm: (projectId: number) => `captured-video/${projectId}/confirm`,
  },
  predict: {
    // Batch auto-annotation. The API owns the RECORD, not the dispatch: the
    // browser still POSTs one run per image to the routes below, each carrying
    // the batchId this returns.
    autoAnnotateBatch: (projectId: number) =>
      `predict/${projectId}/auto-annotate/batch`,
    activeAutoAnnotateBatch: (projectId: number) =>
      `predict/${projectId}/auto-annotate/batch/active`,
    // Robopipe Vision: one synchronous model turn per call. The first POST
    // opens a conversation for the image; feedback continues it over the
    // stream route below.
    vision: (projectId: number, taskId: number) =>
      `predict/${projectId}/${taskId}/vision`,
    // The same two turns, delivered as an NDJSON event stream instead of one
    // response at the end. Used by the labeller's own runs, where somebody is
    // watching the canvas; the batch fan-out keeps the plain route above.
    visionStream: (projectId: number, taskId: number) =>
      `predict/${projectId}/${taskId}/vision/stream`,
    visionFeedbackStream: (
      projectId: number,
      taskId: number,
      conversationId: number,
    ) =>
      `predict/${projectId}/${taskId}/vision/${conversationId}/feedback/stream`,
    // Saves a feedback turn the model kept out of the project notes.
    visionTurnNote: (
      projectId: number,
      taskId: number,
      conversationId: number,
      turn: number,
    ) =>
      `predict/${projectId}/${taskId}/vision/${conversationId}/turns/${turn}/note`,
    visionConversation: (
      projectId: number,
      taskId: number,
      conversationId: number,
    ) => `predict/${projectId}/${taskId}/vision/${conversationId}`,
  },
  // Robopipe Vision project notes: every feedback turn, saved verbatim, plus
  // notes added by hand; all sent with every later run. GET lists them and POST
  // adds one (both admin-only); PATCH edits and DELETE removes one.
  visionNotes: {
    list: (projectId: number) => `projects/${projectId}/vision-notes`,
    one: (projectId: number, noteId: number) =>
      `projects/${projectId}/vision-notes/${noteId}`,
  },
  // A Robopipe Vision suggestion for a label description; nothing is saved.
  labelDescriptionSuggestion: (projectId: number) =>
    `projects/${projectId}/label-description-suggestion`,
  // One aggregate document per project: config (engine + default mode),
  // label descriptions and server availability.
  preAnnotateSettings: (projectId: number) =>
    `projects/${projectId}/pre-annotate-settings`,
  analytics: {
    datasetStats: (projectId: number) => `analytics/${projectId}/dataset-stats`,
  },
  hyperparamSuggestion: (projectId: number) =>
    `hyperparam-suggestion/${projectId}`,
  confidenceReport: {
    report: (projectId: number) => `confidence-report/${projectId}`,
    regions: (projectId: number, taskId: number) =>
      `confidence-report/${projectId}/regions/${taskId}`,
  },
} as const;
